import React, {useContext, useEffect, useMemo} from "react";

// Models
import {
    CarouselKey,
    CarouselDefinitions, ICarouselDefinition,
} from "../../../Models/Interfaces/ICarouselDefinition";

// Context
import { UserAuthenticationContext } from "../../../Context/UserAuthenticationContext";
import { GlobalInterfaceContext } from "../../../Context/GlobalInterfaceContext";

// Components
import SliderFeaturedContent from "../../../Components/Layouts/Content/SliderFeaturedContent";
import CarouselPortrait from "../../../Components/Layouts/Content/Carousels/CarouselPortrait";
import CarouselLandscape from "../../../Components/Layouts/Content/Carousels/CarouselLandscape";
import PortraitPresenterItem from "../../../Components/Layouts/Content/Watch/Series/PortraitPresenterItem";
import UserOnboarding from "../../../Components/GlobalUI/UserOnboarding";

// Hooks
import useFetchCoreCollection from "../../../Hooks/Content/Watch/Series/useFetchCoreCollection";
import useFetchJustReleased from "../../../Hooks/Content/Watch/VideoFeed/useFetchJustReleased";
import useFetchFilms from "../../../Hooks/Content/Watch/VideoFeed/useFetchFilms";
import useFetchTopRated from "../../../Hooks/Content/Watch/VideoFeed/useFetchTopRated";
import useFetchCategoryMixedContent from "../../../Hooks/Content/Watch/MixedContentFeed/useFetchCategoryMixedContent";
import userFetchUserFavoriteContent from '../../../Hooks/User/useFetchUserFavoriteContent';
import useFetchVideosBySeries from "../../../Hooks/Content/Watch/VideoFeed/useFetchVideosBySeries";
import useFetchContinueWatching from "../../../Hooks/Content/Watch/VideoFeed/useFetchContinueWatching"; // <-- New Hook Import

// Helpers
import {SetTitle} from "../../../Helpers/PageMetadata";
import {FavoritesToCarouselItems} from "../../../Helpers/DataConversion/FavoritesToCarousel";
import {transformContinueWatchingToCarouselContent} from "../../../Helpers/DataConversion/ConvertToCarouselContentItem";

function Browse() {

    // Context
    const
        globalInterfaceContext = useContext(GlobalInterfaceContext),
        { userData } = useContext(UserAuthenticationContext),

        // Hooks
        { items: presenters, loading: presentersLoading } = useFetchCoreCollection(),
        { justReleased, justReleasedLoading } = useFetchJustReleased(),
        { films, filmsLoading } = useFetchFilms(12, 0, false),
        { topRated } = useFetchTopRated(),
        { favorites, favoritesLoading } = userFetchUserFavoriteContent(userData?.id),

        // <-- NEW HOOK EXECUTION -->
        { continueWatching, continueWatchingLoading } = useFetchContinueWatching(userData?.id, 15, 0),

        {
            content: trendingContent,
            loading: trendingLoading
        } = useFetchVideosBySeries(185, 12, 0, "desc", true),
        {
            content: consciousnessContent,
            loading: consciousnessLoading,
        } = useFetchCategoryMixedContent(5, 12, 0, false, "desc", true),
        {
            content: healthContent,
            loading: healthLoading,
        } = useFetchCategoryMixedContent(21, 12, 0, false, "desc", true),
        {
            content: seriesContent,
            loading: seriesLoading,
        } = useFetchCategoryMixedContent(18, 12, 0, false, "desc", true),
        {
            content: davidIckeContent,
            loading: davidIckeLoading,
        } = useFetchCategoryMixedContent(17, 12, 0, false, "desc", true),
        {
            content: docsContent,
            loading: docsLoading,
        } = useFetchCategoryMixedContent(3, 12, 0, false, "desc", true),
        {
            content: occultContent,
            loading: occultLoading,
        } = useFetchCategoryMixedContent(29, 12, 0, false, "desc", true),

        // User preferences
        userPrefString: string | undefined = userData?.person?.PrefCategories,

        isCarouselKey = (k: string): k is CarouselKey => {
            return k in CarouselDefinitions;
        },

        userPrefs: CarouselKey[] = userPrefString
            ? userPrefString.split(",").filter(isCarouselKey)
            : [],

        forcedCarousels: CarouselKey[] = [
            "CoreCollection",
            "Trending",
            "JustReleased"
        ],

        allDefinitionKeys = Object.keys(CarouselDefinitions) as CarouselKey[],

        userPrefsWithoutForced = userPrefs.filter(
            (k) => !forcedCarousels.includes(k)
        ),

        leftoverCarousels = allDefinitionKeys.filter(
            (k) => !forcedCarousels.includes(k) && !userPrefsWithoutForced.includes(k)
        ),

        finalLayout: CarouselKey[] = [
            ...forcedCarousels,
            ...userPrefsWithoutForced,
            ...leftoverCarousels,
        ],

        getCarouselDataByKey = (key: CarouselKey) => {
            switch (key) {
                case "CoreCollection":
                    return { items: presenters, loading: presentersLoading };
                case "JustReleased":
                    return { items: justReleased ?? undefined, loading: justReleasedLoading };
                case "Trending":
                    return { items: trendingContent, loading: trendingLoading };
                case "TopRated":
                    return { items: topRated, loading: false };
                case "DavidIcke":
                    return { items: davidIckeContent, loading: davidIckeLoading };
                case "OriginalFilms":
                    return { items: films, loading: filmsLoading };
                case "OriginalSeries":
                    return { items: seriesContent, loading: seriesLoading };
                case "Consciousness":
                    return { items: consciousnessContent, loading: consciousnessLoading };
                case "HealthAndWellbeing":
                    return { items: healthContent, loading: healthLoading };
                case "Documentaries":
                    return { items: docsContent, loading: docsLoading };
                case "OccultAndHiddenKnowledge":
                    return { items: occultContent, loading: occultLoading };
                default:
                    return { items: [], loading: false };
            }
        },

        finalCarousels = finalLayout.map((key) => ({
            key,
            def: CarouselDefinitions[key],
        })),

        renderCarouselItem = (key: CarouselKey, def: ICarouselDefinition) => {
            const {items, loading} = getCarouselDataByKey(key);

            if (def.componentType === "portrait") {
                return (
                    <div className="page-browse__carousel-container" key={key}>
                        <CarouselPortrait
                            heading={def.heading}
                            isDarkMode={globalInterfaceContext.isDarkMode}
                            carouselItems={loading ? undefined : items}
                            buttonText={def.buttonText}
                            buttonLink={def.buttonLink}
                            renderItem={
                                key === "CoreCollection"
                                    ? (item, isItemLoading) => (
                                        <PortraitPresenterItem
                                            item={item}
                                            isLoading={isItemLoading}
                                        />
                                    )
                                    : undefined
                            }
                        />
                    </div>
                );
            } else {
                return (
                    <div className="page-browse__carousel-container" key={key}>
                        <CarouselLandscape
                            heading={def.heading}
                            accentColor={def.accentColor}
                            isDarkMode={globalInterfaceContext.isDarkMode}
                            carouselItems={loading ? undefined : items}
                            buttonText={def.buttonText}
                            buttonLink={def.buttonLink}
                        />
                    </div>
                );
            }
        };

    const favoritesItems = useMemo(() => {
        return FavoritesToCarouselItems(favorites?.items);
    }, [favorites]);

    // Extract and transform the items for the Carousel Component
    const continueWatchingItems = useMemo(() => {
        if (!continueWatching?.items) return [];
        return continueWatching.items.map(transformContinueWatchingToCarouselContent);
    }, [continueWatching]);

    useEffect(() => {
        SetTitle("Watch Ickonic Films & Series");
    }, []);

    return (
        <>
            <UserOnboarding isDarkMode={globalInterfaceContext.isDarkMode} />

            <div className="page page--browse">
                <>
                    <SliderFeaturedContent
                        sliderId={3}
                        classNameInner="front-page"
                        userDetails={userData}
                    />

                    {/* render WeeklyShows + Trending + JustReleased */}
                    {finalCarousels.slice(0, 3).map(({key, def}) => renderCarouselItem(key, def))}

                    {/* CONTINUE WATCHING CAROUSEL */}
                    {userData?.id && continueWatchingItems.length > 0 && (
                        <div className="page-browse__carousel-container" key="continue-watching">
                            <CarouselLandscape
                                heading="Continue Watching"
                                isDarkMode={globalInterfaceContext.isDarkMode}
                                carouselItems={continueWatchingLoading ? undefined : continueWatchingItems}
                                buttonText=""
                                buttonLink=""
                            />
                        </div>
                    )}

                    {/* FAVORITES CAROUSEL */}
                    {userData?.id && (
                        <div className="page-browse__carousel-container" key="user-favorites">
                            <CarouselLandscape
                                heading="Favorites"
                                isDarkMode={globalInterfaceContext.isDarkMode}
                                carouselItems={favoritesLoading ? undefined : favoritesItems}
                                buttonText=""
                                buttonLink=""
                            />
                        </div>
                    )}

                    {/* then categories and everything else */}
                    {finalCarousels.slice(3).map(({key, def}) => renderCarouselItem(key, def))}
                </>
            </div>
        </>
    );
}

export default Browse;
